import React, { useMemo } from "react";

import { IconDefinition } from "@fortawesome/pro-regular-svg-icons";
import { validate as validateUUID } from "uuid";

import { DisplayDateTime } from "src/base-components/Date";
import { PillVariants } from "src/base-components/Pill";
import { TagPill } from "src/base-components/TagPill";
import { TruncatedIdPill } from "src/base-components/TruncatedIdPill";
import { CaseStatusPill } from "src/cases/common/CaseStatusPill";
import { getSubValueDisplayName } from "src/cases/utils";
import {
  CaseEnvironment,
  CasePropertiesValue,
} from "src/clients/case-management";
import { EntityDetailsWindowPill } from "src/entities/entityView/EntityDetailsWindowPill";
import { useEntityDisplayValues } from "src/entities/useEntityDisplayValues";
import { renderUrlValue } from "src/entitiesEventsCommon/propertyRenderers";
import { Environment } from "src/hooks/useEnvironment";
import { formatNumber } from "src/utils/numbers";
import { assertUnreachable } from "src/utils/typeUtils";

const renderEnum = (
  value: string,
  schema: Extract<CasePropertiesValue, { type: "enum" }>,
  caseProperties: Record<string, unknown>,
  propertyKey: string,
): React.ReactNode => {
  const enumValue = schema.values.find((v) => v.value === value);
  const subStatus = caseProperties.sub_status as string | undefined;

  if (propertyKey === "status") {
    return (
      <CaseStatusPill
        status={enumValue?.display_name || value}
        subStatus={
          getSubValueDisplayName(value, subStatus, schema) || subStatus
        }
        variant={enumValue?.color as PillVariants}
      />
    );
  }

  return <TagPill size="sm" value={enumValue || value} />;
};

export const renderByType = ({
  value,
  schema,
  caseProperties,
  key,
  icon,
  caseEnv,
  entityDisplayMap,
}: {
  value: unknown;
  schema: CasePropertiesValue;
  caseProperties: Record<string, unknown>;
  key: string;
  icon?: IconDefinition;
  caseEnv?: CaseEnvironment;
  entityDisplayMap?: Record<string, string>;
}): React.ReactNode => {
  switch (schema.type) {
    case "string":
      if (typeof value === "string" && validateUUID(value)) {
        return <TruncatedIdPill feedback="inline-only-checkmark" id={value} />;
      }
      return <span className="font-inter-normal-12px">{String(value)}</span>;

    case "datetime":
      return (
        <span className="font-inter-normal-12px">
          {typeof value === "string" ? (
            <DisplayDateTime value={value} />
          ) : (
            String(value)
          )}
        </span>
      );

    case "tags":
      if (Array.isArray(value)) {
        return (
          <div className="flex flex-wrap gap-1">
            {value.map((tag, index) => {
              const tagValue = schema.values?.find((v) => v.value === tag);
              return <TagPill key={index} value={tagValue || String(tag)} />;
            })}
          </div>
        );
      }
      return <span className="font-inter-normal-12px">{String(value)}</span>;

    case "enum": {
      return renderEnum(String(value), schema, caseProperties, key);
    }

    case "event_ref": {
      return (
        <TruncatedIdPill feedback="inline-only-checkmark" id={String(value)} />
      );
    }

    case "entity_ref": {
      const entityId = value as string;
      return (
        <EntityDetailsWindowPill
          entityIcon={icon}
          environment={caseEnv as Environment}
          id={entityId}
          name={entityDisplayMap?.[entityId] || entityId}
          schemaId={schema.entity_type}
          isPinnable
        />
      );
    }

    case "integer":
    case "number":
      return (
        <span className="font-inter-normal-12px">
          {typeof value === "number" ? formatNumber(value) : String(value)}
        </span>
      );

    case "url":
      return value ? renderUrlValue(value) : null;

    default:
      assertUnreachable(schema);
  }
};

export const useCaseEntityDisplayMap = ({
  caseProperties,
  schemaProperties,
  environment,
}: {
  caseProperties: Record<string, unknown> | undefined;
  schemaProperties: Record<string, CasePropertiesValue> | undefined;
  environment: Environment;
}) => {
  // keep-memo(react-compiler): reference-compared dep of useEntityDisplayValues (non-compiled entities/)
  const entityRefs = useMemo(() => {
    if (!caseProperties || !schemaProperties) return [];

    return Object.entries(schemaProperties)
      .filter(
        (
          entry,
        ): entry is [
          string,
          Extract<CasePropertiesValue, { type: "entity_ref" }>,
        ] => entry[1].type === "entity_ref",
      )
      .map(([key, schema]) => {
        const entityId = caseProperties[key];
        if (!entityId || typeof entityId !== "string") return null;
        return { id: entityId, entity_type: schema.entity_type };
      })
      .filter((ref): ref is NonNullable<typeof ref> => ref !== null);
  }, [caseProperties, schemaProperties]);

  return useEntityDisplayValues({
    entityRefs,
    environment,
    enabled: entityRefs.length > 0,
  });
};
